import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, AlertCircle, Tag, Layers, Calendar, Hash, Activity,
  MessageSquare, FileText, AlertTriangle, Star, Brain,
  Target, Radio, History, TrendingUp, Link2, Plus, Trophy,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useTicker, useTheme } from '../hooks/useMentions';
import { useSecurities } from '../hooks/useSecurities';
import { useCalls, useScorecardSummary } from '../hooks/useCalls';
import { actionBadge, confidenceBadge, severityStyle } from './reportHelpers';
import {
  outcomeLabel, outcomeBadge, effectiveReturn, returnColor, formatPct,
  formatPrice as formatCallPrice, formatEntry, horizonLabel, daysOpen,
} from './callHelpers';
import TickerPriceChart from './TickerPriceChart';
import WatchlistPinButton from './WatchlistPinButton';

/**
 * Detail view for /mentions/tickers/:symbol and /mentions/themes/:slug.
 * Same shell, slight content difference between the two: tickers have
 * directional callsToAction + alerts + highlights, themes just have the
 * report-theme entries.
 */
const MentionDetail = () => {
  const { kind, key } = useParams();
  const navigate = useNavigate();

  if (kind === 'tickers') return <TickerDetailView symbol={key} navigate={navigate} />;
  if (kind === 'themes')  return <ThemeDetailView  slug={key}   navigate={navigate} />;

  return (
    <BackShell onBack={() => navigate('/mentions')}>
      <div className="empty-state">
        <AlertCircle size={48} />
        <p>Unknown detail kind: {kind}</p>
      </div>
    </BackShell>
  );
};

// --- Ticker detail --------------------------------------------------

const TickerDetailView = ({ symbol, navigate }) => {
  const { item: ticker, loading, error } = useTicker(symbol);
  const location = useLocation();

  // The URL may name an alias (clicking "LXS" on a report badge) while the
  // page is really the canonical security. Converge the address bar so LXS
  // and LNXSY don't accumulate as two bookmarks for one page.
  const canonical = ticker?.canonicalSymbol;
  useEffect(() => {
    if (!canonical) return;
    if (canonical.toUpperCase() === (symbol || '').toUpperCase()) return;
    navigate(`/mentions/tickers/${encodeURIComponent(canonical)}`, {
      replace: true,
      state: location.state,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canonical, symbol]);

  if (loading) return <Spinner />;
  if (error === 'not-found') {
    return (
      <BackShell onBack={() => navigate('/mentions/tickers')}>
        <div className="empty-state">
          <AlertCircle size={48} />
          <p>Ticker <strong>{symbol}</strong> not found.</p>
          <p style={{ fontSize: '0.875rem' }}>
            It may not have been mentioned in any analyzed message yet.
          </p>
        </div>
        <LinkHoldingPanel
          symbol={symbol}
          holdingName={location.state?.name}
          fromPortfolio={Boolean(location.state?.fromPortfolio)}
          navigate={navigate}
        />
      </BackShell>
    );
  }
  if (error) return <ErrorBlock message={error} onBack={() => navigate('/mentions/tickers')} />;
  if (!ticker) return null;

  const firstSeen = parseDate(ticker.firstSeen);
  const lastSeen  = parseDate(ticker.lastSeen);
  const lastReportDate = parseDate(ticker.latestReportDate);

  return (
    <div>
      <BackShell onBack={() => navigate('/mentions/tickers')} label="Back to tickers" />

      {/* Hero */}
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
          <Tag size={16} />
          <span>Ticker</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
          <h1 style={{ margin: 0, lineHeight: 1.3, fontSize: '2rem', color: 'var(--primary-color)' }}>
            {ticker.symbol}
          </h1>
          {ticker.displayName && (
            <span style={{ fontSize: '1.0625rem', color: 'var(--text-secondary)' }}>
              {ticker.displayName}
            </span>
          )}
          <WatchlistPinButton kind="ticker" itemKey={ticker.symbol} />
        </div>

        {/* Which other listings this page is folding in. Without it the
            merged mention counts look inexplicably high for the symbol. */}
        {ticker.aliases?.length > 0 && (
          <div style={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.375rem',
            fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: '0.5rem',
          }}>
            <Link2 size={13} />
            <span>Also known as</span>
            {ticker.aliases.map((a) => <span key={a} className="badge">{a}</span>)}
          </div>
        )}

        {/* Latest guidance hero block */}
        {ticker.latestAction ? (
          <div style={{
            marginTop: '1rem', padding: '1rem',
            background: 'var(--bg-tertiary)',
            borderLeft: `4px solid ${actionAccentVar(ticker.latestAction)}`,
            borderRadius: '6px',
          }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Latest guidance
              </span>
              <span className={`badge ${actionBadge(ticker.latestAction)}`}>
                {ticker.latestAction}
              </span>
              {ticker.latestConfidence && (
                <span className={`badge ${confidenceBadge(ticker.latestConfidence)}`}>
                  {ticker.latestConfidence}
                </span>
              )}
              {ticker.latestPriority != null && (
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                  Priority {ticker.latestPriority}
                </span>
              )}
              {lastReportDate && (
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginLeft: 'auto' }}>
                  {format(lastReportDate, 'EEE MMM dd, yyyy')}
                </span>
              )}
            </div>
            {ticker.latestRationale && (
              <p style={{ margin: 0, fontSize: '0.9375rem', lineHeight: 1.55 }}>
                {ticker.latestRationale}
              </p>
            )}
            {ticker.latestReportId && (
              <button
                type="button"
                onClick={() => navigate(`/analysis/${encodeURIComponent(ticker.latestReportId)}`)}
                className="btn btn-secondary"
                style={{ marginTop: '0.75rem', fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}
              >
                Open source report
              </button>
            )}
          </div>
        ) : ticker.lastDirectMention ? (
          <LatestRawMentionBlock mention={ticker.lastDirectMention} navigate={navigate} />
        ) : (
          <div style={{
            marginTop: '1rem', padding: '0.75rem 1rem',
            background: 'var(--severity-info-bg)',
            borderLeft: '4px solid var(--severity-info-border)',
            borderRadius: '4px',
            fontSize: '0.9375rem',
          }}>
            <strong>No mentions yet.</strong> This symbol hasn't been mentioned in any
            analyzed message, transcript, or daily report.
          </div>
        )}

        {/* Stats strip */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '1.25rem',
          marginTop: '1rem', paddingTop: '1rem',
          borderTop: '1px solid var(--border-color)',
          fontSize: '0.875rem', color: 'var(--text-secondary)',
        }}>
          <Stat icon={<Hash size={14} />} label="Message mentions" value={ticker.messageMentionCount} />
          <Stat icon={<Activity size={14} />} label="Calls to action" value={ticker.callToActionCount} />
          {ticker.transcriptMentionCount > 0 && (
            <Stat icon={<Layers size={14} />} label="Transcript mentions" value={ticker.transcriptMentionCount} />
          )}
          {firstSeen && <Stat icon={<Calendar size={14} />} label="First seen" value={format(firstSeen, 'MMM dd, yyyy')} />}
          {lastSeen  && <Stat icon={<Calendar size={14} />} label="Last seen"  value={format(lastSeen,  'MMM dd, yyyy')} />}
        </div>
      </div>

      {/* Price action -- Schwab for stocks/ETFs, CoinGecko fallback for crypto.
          Renders nothing when neither provider has data. */}
      <TickerPriceChart symbol={ticker.symbol} />

      {/* Scorecard: every call on this name, with outcomes. Renders nothing
          until at least one call has been tracked. */}
      <TickerScorecard symbol={ticker.symbol} navigate={navigate} />

      {/* Current trade plan (entry / TP / SL / horizon). Surfaces only when
          Russ has stated a trade plan in a raw source. */}
      {ticker.tradePlan && (
        <Section title="Current trade plan" icon={<Target size={16} />}>
          <TradePlanCard plan={ticker.tradePlan} navigate={navigate} />
        </Section>
      )}

      {/* Trade plan history (audit trail of how the plan evolved). */}
      {ticker.tradePlanHistory && ticker.tradePlanHistory.length > 0 && (
        <Section title="Trade plan history" icon={<History size={16} />}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {ticker.tradePlanHistory.map((p, i) => (
              <TradePlanCard key={`${p.sourceId}-${i}`} plan={p} navigate={navigate} compact />
            ))}
          </div>
        </Section>
      )}

      {/* Recent direct mentions -- the raw signal, separate from interpreted guidance. */}
      {ticker.recentDirectMentions && ticker.recentDirectMentions.length > 0 && (
        <Section title="Recent direct mentions (raw)" icon={<Radio size={16} />}>
          <DirectMentionsList mentions={ticker.recentDirectMentions} navigate={navigate} />
        </Section>
      )}

      {/* Directional change timeline -- only entries where the action changed. */}
      {ticker.guidanceTimeline && ticker.guidanceTimeline.length > 0 && (
        <Section title="Directional changes" icon={<TrendingUp size={16} />}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {ticker.guidanceTimeline.map((e, i) => (
              <button
                key={`${e.reportId}-${i}`}
                type="button"
                onClick={() => navigate(`/analysis/${encodeURIComponent(e.reportId)}`)}
                className="analysis-list-card mention-row"
                style={{ borderLeft: `4px solid ${actionAccentVar(e.action)}`, padding: '0.625rem 0.875rem' }}
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem', marginBottom: e.rationale ? '0.25rem' : 0 }}>
                  {e.priorAction && (
                    <>
                      <span className={`badge ${actionBadge(e.priorAction)}`}>{e.priorAction}</span>
                      <span style={{ color: 'var(--text-secondary)' }}>→</span>
                    </>
                  )}
                  <span className={`badge ${actionBadge(e.action)}`}>{e.action}</span>
                  <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    {format(parseISO(e.reportDate), 'MMM dd, yyyy')}
                  </span>
                </div>
                {e.rationale && (
                  <p style={{ margin: 0, fontSize: '0.875rem', lineHeight: 1.5 }}>{e.rationale}</p>
                )}
              </button>
            ))}
          </div>
        </Section>
      )}

      {/* CallsToAction history */}
      {ticker.callsToAction && ticker.callsToAction.length > 0 && (
        <Section title="Guidance history" icon={<Activity size={16} />}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {ticker.callsToAction.map((c) => (
              <button
                key={c.reportId + c.reportDate}
                type="button"
                onClick={() => navigate(`/analysis/${encodeURIComponent(c.reportId)}`)}
                className="analysis-list-card mention-row"
                style={{
                  borderLeft: `4px solid ${actionAccentVar(c.action)}`,
                  padding: '0.625rem 0.875rem',
                }}
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem', marginBottom: c.rationale ? '0.25rem' : 0 }}>
                  <span className={`badge ${actionBadge(c.action)}`}>{c.action}</span>
                  {c.confidence && <span className={`badge ${confidenceBadge(c.confidence)}`}>{c.confidence}</span>}
                  {c.priority != null && <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>P{c.priority}</span>}
                  {c.targetPrice != null && <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>target ${Number(c.targetPrice).toLocaleString()}</span>}
                  <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    {format(parseISO(c.reportDate), 'MMM dd, yyyy')}
                  </span>
                </div>
                {c.rationale && (
                  <p style={{ margin: 0, fontSize: '0.875rem', lineHeight: 1.5, color: 'var(--text-primary)' }}>
                    {c.rationale}
                  </p>
                )}
              </button>
            ))}
          </div>
        </Section>
      )}

      {/* Alerts */}
      {ticker.alerts && ticker.alerts.length > 0 && (
        <Section title="Portfolio alerts" icon={<AlertTriangle size={16} />}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            {ticker.alerts.map((a, i) => (
              <div key={i} style={{ ...severityStyle(a.severity), padding: '0.625rem 0.875rem', borderRadius: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.25rem' }}>
                  <strong>{(a.severity || 'info').toUpperCase()}</strong>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    {format(parseISO(a.reportDate), 'MMM dd, yyyy')}
                  </span>
                </div>
                <span style={{ fontSize: '0.875rem', lineHeight: 1.5 }}>{a.message}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Highlights */}
      {ticker.highlights && ticker.highlights.length > 0 && (
        <Section title="Highlights" icon={<Star size={16} />}>
          <ul style={{ margin: 0, paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            {ticker.highlights.map((h, i) => (
              <li key={i} style={{ fontSize: '0.9375rem', lineHeight: 1.55 }}>
                {h.note}
                <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  ({format(parseISO(h.reportDate), 'MMM dd')})
                </span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Recent messages */}
      {ticker.recentMessages && ticker.recentMessages.length > 0 && (
        <Section title="Recent message mentions" icon={<MessageSquare size={16} />}>
          <MentionMessageList messages={ticker.recentMessages} />
        </Section>
      )}

      {/* Transcripts */}
      {ticker.transcripts && ticker.transcripts.length > 0 && (
        <Section title="Transcript mentions" icon={<FileText size={16} />}>
          <MentionTranscriptList transcripts={ticker.transcripts} navigate={navigate} />
        </Section>
      )}
    </div>
  );
};

// --- Theme detail ---------------------------------------------------

const ThemeDetailView = ({ slug, navigate }) => {
  const { item: theme, loading, error } = useTheme(slug);

  if (loading) return <Spinner />;
  if (error === 'not-found') {
    return (
      <BackShell onBack={() => navigate('/mentions/themes')}>
        <div className="empty-state">
          <AlertCircle size={48} />
          <p>Theme <strong>{slug}</strong> not found.</p>
        </div>
      </BackShell>
    );
  }
  if (error) return <ErrorBlock message={error} onBack={() => navigate('/mentions/themes')} />;
  if (!theme) return null;

  const firstSeen = parseDate(theme.firstSeen);
  const lastSeen  = parseDate(theme.lastSeen);
  const lastReportDate = parseDate(theme.latestReportDate);

  return (
    <div>
      <BackShell onBack={() => navigate('/mentions/themes')} label="Back to themes" />

      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
          <Layers size={16} />
          <span>Theme</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
          <h1 style={{ margin: 0, lineHeight: 1.3 }}>
            {theme.displayName || theme.slug}
          </h1>
          <WatchlistPinButton kind="theme" itemKey={theme.slug} label={theme.displayName || theme.slug} />
        </div>
        <code style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{theme.slug}</code>

        {theme.latestReportTitle && (
          <div style={{
            marginTop: '1rem', padding: '1rem',
            background: 'var(--bg-tertiary)',
            borderLeft: '4px solid var(--primary-color)',
            borderRadius: '6px',
          }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.375rem', display: 'flex', justifyContent: 'space-between' }}>
              <span><Brain size={12} style={{ display: 'inline', marginRight: '0.25rem' }} />Latest report mention</span>
              {lastReportDate && <span>{format(lastReportDate, 'MMM dd, yyyy')}</span>}
            </div>
            <strong style={{ display: 'block', marginBottom: '0.25rem' }}>{theme.latestReportTitle}</strong>
            {theme.latestReportBody && (
              <p style={{ margin: 0, fontSize: '0.9375rem', lineHeight: 1.55 }}>{theme.latestReportBody}</p>
            )}
            {theme.latestReportId && (
              <button
                type="button"
                onClick={() => navigate(`/analysis/${encodeURIComponent(theme.latestReportId)}`)}
                className="btn btn-secondary"
                style={{ marginTop: '0.75rem', fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}
              >
                Open source report
              </button>
            )}
          </div>
        )}

        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '1.25rem',
          marginTop: '1rem', paddingTop: '1rem',
          borderTop: '1px solid var(--border-color)',
          fontSize: '0.875rem', color: 'var(--text-secondary)',
        }}>
          <Stat icon={<Hash size={14} />} label="Message mentions" value={theme.messageMentionCount} />
          <Stat icon={<Activity size={14} />} label="Report appearances" value={theme.reportThemeCount} />
          {theme.transcriptMentionCount > 0 && (
            <Stat icon={<Layers size={14} />} label="Transcript mentions" value={theme.transcriptMentionCount} />
          )}
          {firstSeen && <Stat icon={<Calendar size={14} />} label="First seen" value={format(firstSeen, 'MMM dd, yyyy')} />}
          {lastSeen  && <Stat icon={<Calendar size={14} />} label="Last seen"  value={format(lastSeen,  'MMM dd, yyyy')} />}
        </div>
      </div>

      {theme.reportThemes && theme.reportThemes.length > 0 && (
        <Section title="Report appearances" icon={<Brain size={16} />}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {theme.reportThemes.map((r) => (
              <button
                key={r.reportId + r.reportDate}
                type="button"
                onClick={() => navigate(`/analysis/${encodeURIComponent(r.reportId)}`)}
                className="analysis-list-card mention-row"
                style={{ padding: '0.625rem 0.875rem' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.25rem' }}>
                  <strong style={{ fontSize: '0.9375rem' }}>{r.title}</strong>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    {format(parseISO(r.reportDate), 'MMM dd, yyyy')}
                  </span>
                </div>
                {r.body && (
                  <p style={{ margin: 0, fontSize: '0.875rem', lineHeight: 1.5, color: 'var(--text-primary)' }}>
                    {r.body}
                  </p>
                )}
              </button>
            ))}
          </div>
        </Section>
      )}

      {theme.recentDirectMentions && theme.recentDirectMentions.length > 0 && (
        <Section title="Recent direct mentions (raw)" icon={<Radio size={16} />}>
          <DirectMentionsList mentions={theme.recentDirectMentions} navigate={navigate} />
        </Section>
      )}

      {theme.recentMessages && theme.recentMessages.length > 0 && (
        <Section title="Recent message mentions" icon={<MessageSquare size={16} />}>
          <MentionMessageList messages={theme.recentMessages} />
        </Section>
      )}

      {theme.transcripts && theme.transcripts.length > 0 && (
        <Section title="Transcript mentions" icon={<FileText size={16} />}>
          <MentionTranscriptList transcripts={theme.transcripts} navigate={navigate} />
        </Section>
      )}
    </div>
  );
};

// --- Projection-driven blocks ---------------------------------------

const LatestRawMentionBlock = ({ mention, navigate }) => {
  const date = parseDate(mention.sourceDate);
  return (
    <div style={{
      marginTop: '1rem', padding: '1rem',
      background: 'var(--bg-tertiary)',
      borderLeft: '4px solid var(--primary-color)',
      borderRadius: '6px',
    }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Latest from Russ (raw)
        </span>
        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
          {mention.sourceType === 'transcript' ? 'transcript' : 'message'}
        </span>
        {date && (
          <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginLeft: 'auto' }}>
            {format(date, 'EEE MMM dd, yyyy')}
          </span>
        )}
      </div>
      {mention.quote && (
        <p style={{ margin: 0, fontSize: '0.9375rem', lineHeight: 1.55 }}>
          {mention.quote}
        </p>
      )}
      <button
        type="button"
        onClick={() => navigate(linkForSource(mention))}
        className="btn btn-secondary"
        style={{ marginTop: '0.75rem', fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}
      >
        Open source {mention.sourceType === 'transcript' ? 'transcript' : 'message'}
      </button>
    </div>
  );
};

// --- Scorecard section ------------------------------------------------

/**
 * Russ's track record on this one name. Leads with the numbers, then each
 * call newest-first with its levels and what happened. Plan and guidance
 * calls are labeled so the reader can tell Russ's own calls from the
 * report's reading of them.
 */
const TickerScorecard = ({ symbol, navigate }) => {
  const { summary } = useScorecardSummary({ symbol });
  const { calls, loading } = useCalls({ symbol, limit: 50 });

  if (loading || calls.length === 0) return null;
  const stats = summary?.stats;

  return (
    <Section title="Scorecard" icon={<Trophy size={16} />}>
      {stats && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '1.25rem', marginBottom: '0.875rem',
          fontSize: '0.875rem', color: 'var(--text-secondary)',
        }}>
          <Stat label="Hit rate" value={stats.hitRatePct != null ? `${stats.hitRatePct}% (${stats.wins}/${stats.resolved})` : 'no resolutions yet'} />
          <Stat label="Avg return" value={<span style={{ color: returnColor(stats.avgReturnPct) }}>{formatPct(stats.avgReturnPct)}</span>} />
          <Stat label="Calls" value={`${stats.total} (${stats.open} open)`} />
          {stats.avgCheckpointReturnPct?.['30'] != null && (
            <Stat label="Avg 30d" value={<span style={{ color: returnColor(stats.avgCheckpointReturnPct['30']) }}>{formatPct(stats.avgCheckpointReturnPct['30'])}</span>} />
          )}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {calls.map((c) => <CallCard key={c.id} call={c} navigate={navigate} />)}
      </div>
      <button
        type="button"
        onClick={() => navigate('/scorecard')}
        className="btn btn-secondary"
        style={{ marginTop: '0.75rem', fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}
      >
        Open full scorecard
      </button>
    </Section>
  );
};

const CallCard = ({ call, navigate }) => {
  const ret = effectiveReturn(call);
  const opened = parseDate(call.openedAt);
  const entry = formatEntry(call);
  const closeOrNow = call.status === 'closed' && call.outcome ? call.outcome.closePrice : call.currentPrice;
  const days = daysOpen(call);
  const source = call.source || {};
  const accent = call.status !== 'closed' ? 'var(--primary-color)'
    : call.outcome?.isWin === true ? 'var(--action-buy)'
    : call.outcome?.isWin === false ? 'var(--action-sell)'
    : 'var(--action-hold)';

  const fields = [
    entry && { label: 'Entry', value: entry },
    call.takeProfit?.length > 0 && { label: call.takeProfit.length > 1 ? 'Take profit' : 'TP', value: call.takeProfit.map(formatCallPrice).join(' / ') },
    call.stopLoss != null && { label: 'Stop loss', value: formatCallPrice(call.stopLoss) },
    call.referencePrice != null && { label: 'Ref', value: formatCallPrice(call.referencePrice) },
    closeOrNow != null && { label: call.status === 'closed' ? 'Close' : 'Now', value: formatCallPrice(closeOrNow) },
    days != null && { label: 'Days', value: days },
  ].filter(Boolean);

  return (
    <div style={{
      padding: '0.75rem 0.875rem',
      background: 'var(--bg-primary)',
      border: '1px solid var(--border-color)',
      borderLeft: `4px solid ${accent}`,
      borderRadius: '6px',
    }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <span className={`badge ${outcomeBadge(call)}`}>{outcomeLabel(call)}</span>
        <span className={`badge ${call.kind === 'guidance' ? 'badge-accent' : 'badge-info'}`}>
          {call.kind === 'guidance' ? 'Report guidance' : "Russ's plan"}
        </span>
        <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
          {call.direction}{call.horizon ? ` · ${horizonLabel(call.horizon)}` : ''}
        </span>
        <strong style={{ color: returnColor(ret), marginLeft: 'auto' }}>{formatPct(ret)}</strong>
        {opened && (
          <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
            {format(opened, 'MMM dd, yyyy')}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem 1.25rem', marginBottom: source.quote ? '0.5rem' : 0 }}>
        {fields.map((f) => (
          <div key={f.label} style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem' }}>
            <span style={{ fontSize: '0.6875rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {f.label}
            </span>
            <strong style={{ fontSize: '0.875rem' }}>{f.value}</strong>
          </div>
        ))}
      </div>
      {(call.levelHits?.length > 0 || call.amendments?.length > 0) && (
        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.375rem' }}>
          {call.levelHits?.map((h) => (
            <span key={`${h.kind}-${h.index}`} style={{ marginRight: '0.75rem' }}>
              {h.kind === 'sl' ? 'SL' : `TP${call.takeProfit?.length > 1 ? h.index + 1 : ''}`} {formatCallPrice(h.level)} hit {h.hitAt ? format(parseDate(h.hitAt), 'MMM dd') : ''}
            </span>
          ))}
          {call.amendments?.length > 0 && (
            <span>{call.amendments.length} amendment{call.amendments.length === 1 ? '' : 's'}</span>
          )}
        </div>
      )}
      {source.quote && (
        <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--text-secondary)', fontStyle: 'italic', lineHeight: 1.5 }}>
          "{source.quote}"
        </p>
      )}
      {call.evaluationError && (
        <p style={{ margin: '0.375rem 0 0', fontSize: '0.75rem', color: 'var(--warning-color)' }}>
          Not evaluated: {call.evaluationError}
        </p>
      )}
      {source.sourceId && (
        <button
          type="button"
          onClick={() => navigate(linkForSource(source))}
          className="btn btn-secondary"
          style={{ marginTop: '0.5rem', fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}
        >
          Open source {source.sourceType === 'report' ? 'report' : source.sourceType === 'transcript' ? 'transcript' : 'message'}
        </button>
      )}
    </div>
  );
};

const TradePlanCard = ({ plan, navigate, compact = false }) => {
  const date = parseDate(plan.sourceDate);
  const fields = [
    plan.horizon && { label: 'Horizon', value: plan.horizon === 'short' ? 'short-term' : 'long-term' },
    (plan.entryLow != null || plan.entryHigh != null) && {
      label: 'Entry',
      value: formatRange(plan.entryLow, plan.entryHigh),
    },
    plan.takeProfit && plan.takeProfit.length > 0 && {
      label: plan.takeProfit.length > 1 ? 'Take profit' : 'TP',
      value: plan.takeProfit.map((v) => formatPrice(v)).join(' / '),
    },
    plan.stopLoss != null && { label: 'Stop loss', value: formatPrice(plan.stopLoss) },
  ].filter(Boolean);

  return (
    <div style={{
      padding: compact ? '0.625rem 0.875rem' : '1rem',
      background: 'var(--bg-primary)',
      border: '1px solid var(--border-color)',
      borderRadius: '6px',
    }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.875rem 1.5rem', marginBottom: plan.rawQuote ? '0.625rem' : 0 }}>
        {fields.map((f) => (
          <div key={f.label} style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem' }}>
            <span style={{ fontSize: '0.6875rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {f.label}
            </span>
            <strong style={{ fontSize: '0.9375rem' }}>{f.value}</strong>
          </div>
        ))}
        <div style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'var(--text-secondary)', alignSelf: 'flex-end' }}>
          {date ? format(date, 'MMM dd, yyyy') : ''}
        </div>
      </div>
      {plan.rawQuote && (
        <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--text-secondary)', fontStyle: 'italic', lineHeight: 1.5 }}>
          "{plan.rawQuote}"
        </p>
      )}
      {!compact && plan.sourceId && (
        <button
          type="button"
          onClick={() => navigate(linkForSource(plan))}
          className="btn btn-secondary"
          style={{ marginTop: '0.625rem', fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}
        >
          Open source
        </button>
      )}
    </div>
  );
};

const DirectMentionsList = ({ mentions, navigate }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
    {mentions.map((m, i) => {
      const date = parseDate(m.sourceDate);
      return (
        <button
          key={`${m.sourceId}-${i}`}
          type="button"
          onClick={() => navigate(linkForSource(m))}
          className="analysis-list-card mention-row"
          style={{ padding: '0.625rem 0.875rem', textAlign: 'left' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.25rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
            <span>{m.sourceType === 'transcript' ? 'Transcript' : 'Telegram'}</span>
            <span>{date ? format(date, 'MMM dd, yyyy h:mm a') : ''}</span>
          </div>
          {m.quote && (
            <p style={{ margin: 0, fontSize: '0.875rem', lineHeight: 1.5, color: 'var(--text-primary)' }}>
              {m.quote}
            </p>
          )}
        </button>
      );
    })}
  </div>
);

const linkForSource = (m) => {
  if (!m || !m.sourceId) return '#';
  if (m.sourceType === 'transcript') return `/transcripts/${encodeURIComponent(m.sourceId)}`;
  if (m.sourceType === 'report')     return `/analysis/${encodeURIComponent(m.sourceId)}`;
  return `/messages?messageId=${encodeURIComponent(m.sourceId)}`;
};

const formatPrice = (n) => {
  if (n == null) return '';
  const v = Number(n);
  if (!Number.isFinite(v)) return '';
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const formatRange = (low, high) => {
  if (low == null && high == null) return '';
  if (low != null && high != null && low !== high) {
    return `${formatPrice(low)} – ${formatPrice(high)}`;
  }
  return formatPrice(low ?? high);
};

// --- Shared bits ----------------------------------------------------

const MentionMessageList = ({ messages }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
    {messages.map((m) => (
      <div key={m.messageId} style={{
        padding: '0.625rem 0.875rem',
        border: '1px solid var(--border-color)',
        borderRadius: '6px',
        background: 'var(--bg-primary)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.25rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
          <code>{m.messageId}</code>
          <span>{format(parseISO(m.date), 'MMM dd, yyyy h:mm a')}</span>
        </div>
        {m.summary && (
          <p style={{ margin: '0 0 0.375rem', fontSize: '0.875rem', lineHeight: 1.5, color: 'var(--text-primary)', fontStyle: 'italic' }}>
            {m.summary}
          </p>
        )}
        {m.snippet && (
          <p style={{ margin: 0, fontSize: '0.8125rem', lineHeight: 1.5, color: 'var(--text-secondary)' }}>
            {m.snippet}
          </p>
        )}
        {m.context && !m.snippet && (
          <p style={{ margin: 0, fontSize: '0.8125rem', lineHeight: 1.5, color: 'var(--text-secondary)' }}>
            {m.context}
          </p>
        )}
      </div>
    ))}
  </div>
);

const MentionTranscriptList = ({ transcripts, navigate }) => (
  <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
    {transcripts.map((t) => (
      <li key={t.transcriptId}>
        <button
          type="button"
          onClick={() => navigate(`/transcripts/${encodeURIComponent(t.transcriptId)}`)}
          className="analysis-list-card mention-row"
          style={{ padding: '0.5rem 0.75rem', width: '100%' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', alignItems: 'baseline' }}>
            <strong style={{ fontSize: '0.9375rem' }}>{t.title || '(untitled transcript)'}</strong>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
              {format(parseISO(t.uploadedAt), 'MMM dd, yyyy')}
            </span>
          </div>
        </button>
      </li>
    ))}
  </ul>
);

const Section = ({ title, icon, children }) => (
  <div className="card" style={{ marginBottom: '1.25rem' }}>
    <h2 style={{
      fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)',
      textTransform: 'uppercase', letterSpacing: '0.05em',
      marginTop: 0, marginBottom: '0.875rem',
      display: 'flex', alignItems: 'center', gap: '0.375rem',
    }}>
      {icon}
      {title}
    </h2>
    {children}
  </div>
);

const Stat = ({ icon, label, value }) => (
  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
    {icon}
    <span style={{ color: 'var(--text-secondary)' }}>{label}:</span>
    <strong style={{ color: 'var(--text-primary)' }}>{value}</strong>
  </div>
);

// --- Linking a holding to a tracked security -------------------------

/**
 * The 404 recovery path, and in practice the main way the alias map gets
 * populated.
 *
 * When a Schwab holding has no detail page it is almost always a cross-listing:
 * the user holds the ADR (LNXSY) while Russ posts about the native listing
 * (LXS). Rather than leaving a dead end and expecting anyone to go find the
 * Securities page, offer the link right here, where the mismatch is visible.
 *
 * Only shown when arriving from Portfolio -- a stray URL typo should get a
 * plain 404, not an invitation to create data.
 */
const LinkHoldingPanel = ({ symbol, holdingName, fromPortfolio, navigate }) => {
  const { securities, save, addAlias } = useSecurities();
  const [choice, setChoice] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  if (!fromPortfolio) return null;

  const run = async (fn) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      navigate(0);  // re-fetch the detail; the resolver cache was invalidated server-side
    } catch (e) {
      setErr(e?.message?.replace(/^API call failed: /, '') || 'Failed to link');
      setBusy(false);
    }
  };

  /**
   * Attach this holding's symbol to an identity Russ's posts already reach.
   * The holding's symbol becomes the ALIAS, not the canonical -- the canonical
   * is immutable and moving it would break existing links.
   */
  const linkToExisting = () => run(() =>
    addAlias(choice, { value: symbol, kind: 'symbol' }));

  /**
   * No identity exists yet. Create one keyed on the holding's own symbol,
   * which is the right canonical anyway: it is what Schwab clears and the only
   * string that can be priced. Aliases for Russ's symbols get added after.
   */
  const createNew = () => run(() =>
    save(symbol, { displayName: holdingName || undefined, tradeableSymbol: symbol }));

  return (
    <div className="card" style={{ marginTop: '1rem' }}>
      <h3 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Link2 size={18} /> Link this holding to a security Russ tracks
      </h3>
      <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
        You hold <strong>{symbol}</strong>{holdingName ? ` (${holdingName})` : ''}, but nothing is
        filed under that symbol. If Russ refers to this company by its native-market ticker,
        link them and this page will show his mentions.
      </p>

      {err && (
        <p style={{ color: 'var(--danger-color, #c0392b)', fontSize: '0.875rem' }}>
          <AlertCircle size={14} style={{ verticalAlign: 'text-bottom' }} /> {err}
        </p>
      )}

      {securities.length > 0 && (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.75rem' }}>
          <select
            className="settings-select"
            value={choice}
            onChange={(e) => setChoice(e.target.value)}
            style={{ flex: '1 1 220px' }}
          >
            <option value="">Choose an existing security...</option>
            {securities.map((s) => (
              <option key={s.canonicalSymbol} value={s.canonicalSymbol}>
                {s.canonicalSymbol}{s.displayName ? ` -- ${s.displayName}` : ''}
              </option>
            ))}
          </select>
          <button className="btn btn-primary" disabled={busy || !choice} onClick={linkToExisting}>
            Link {symbol}
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <button className="btn btn-secondary" disabled={busy} onClick={createNew}>
          <Plus size={14} /> Create a new identity for {symbol}
        </button>
        <button className="btn btn-secondary" disabled={busy} onClick={() => navigate('/securities')}>
          Manage all securities
        </button>
      </div>
    </div>
  );
};

const BackShell = ({ onBack, children, label = 'Back' }) => (
  <div>
    <button
      className="btn btn-secondary"
      onClick={onBack}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', marginBottom: '1rem' }}
    >
      <ArrowLeft size={16} /> {label}
    </button>
    {children}
  </div>
);

const Spinner = () => <div className="loading"><div className="spinner"></div></div>;

const ErrorBlock = ({ message, onBack }) => (
  <BackShell onBack={onBack}>
    <div className="error">
      <AlertCircle size={24} />
      <p>{message}</p>
    </div>
  </BackShell>
);

const parseDate = (raw) => {
  if (!raw) return null;
  try {
    return typeof raw === 'string' ? parseISO(raw) : new Date(raw);
  } catch {
    return null;
  }
};

const actionAccentVar = (action) => {
  const a = (action || '').toUpperCase();
  if (a === 'BUY' || a === 'ADD') return 'var(--action-buy)';
  if (a === 'SELL' || a === 'REDUCE') return 'var(--action-sell)';
  return 'var(--action-hold)';
};

export default MentionDetail;
